import type { ActivityEntry } from '../state/agents.js';

/**
 * Prisma's live status line: what she is doing right now, in her own words.
 * Built from the newest activity of her current turn (the tool she runs and
 * the file it touches, or whether she is thinking or writing), never from
 * tool names, models or anything else behind the scenes.
 */

/** A file name as words: "player-controller.rhai" → "player controller". */
function pretty(path: string): string {
  const base = path.split(/[\\/]/).pop() ?? path;
  return base.replace(/\.(scene|model|sound|pixel|ui|prefab|material|video|anim|rig|outfit)?\.?(json|rhai|wgsl|png|jpe?g|wav|ogg|md|txt)$/i, '').replace(/[-_.]+/g, ' ').trim() || base;
}

/** What a change to `path` means for the game. */
function building(path: string, verb: 'make' | 'tweak'): string {
  const p = path.toLowerCase();
  const name = pretty(path);
  const pick = (make: string, tweak: string) => (verb === 'make' ? make : tweak);
  if (p.endsWith('.scene.json')) return pick('Building your scene', 'Rearranging the scene');
  if (p.endsWith('.prefab.json')) return pick(`Putting together ${name}`, `Adjusting ${name}`);
  if (p.endsWith('.model.json') || p.endsWith('.glb')) return pick(`Sculpting ${name}`, `Reshaping ${name}`);
  if (p.endsWith('.rhai')) return pick(`Scripting ${name}`, `Fine-tuning ${name}`);
  if (p.endsWith('.sound.json') || p.endsWith('.wav') || p.endsWith('.ogg')) return pick(`Composing ${name}`, `Remixing ${name}`);
  if (p.endsWith('.ui.json')) return pick('Designing the screens', 'Polishing the screens');
  if (p.endsWith('.pixel.json') || /\.(png|jpe?g)$/.test(p)) return pick(`Painting ${name}`, `Touching up ${name}`);
  if (p.endsWith('.material.json') || p.endsWith('.wgsl')) return pick(`Mixing the look of ${name}`, `Adjusting the look of ${name}`);
  if (p.endsWith('prism.project.json')) return 'Updating the game settings';
  if (p.endsWith('.md') || p.endsWith('.txt')) return pick('Writing notes', 'Updating notes');
  return pick(`Creating ${name}`, `Tweaking ${name}`);
}

/** One tool call as a line in Prisma's words. */
export function describeTool(tool: string, args: Record<string, unknown> | undefined): string {
  const path = String(args?.path ?? args?.out ?? args?.file ?? args?.to ?? '');
  switch (tool) {
    case 'read_file':
      return path ? `Reading ${pretty(path)}` : 'Looking through your project';
    case 'list_files':
    case 'find_files':
    case 'search_files':
      return 'Looking around your project';
    case 'write_file':
      return path ? building(path, 'make') : 'Building';
    case 'edit_file':
    case 'prism_sound_edit':
      return path ? building(path, 'tweak') : 'Making changes';
    case 'delete_file':
      return 'Tidying up';
    case 'move_file':
      return 'Reorganizing your files';
    case 'prism_docs':
      return 'Checking the engine guide';
    case 'prism_check':
      return 'Making sure everything works';
    case 'prism_screenshot':
      return 'Taking a look at your game';
    case 'prism_model_render':
      return 'Taking a look at the model';
    case 'prism_run_game':
    case 'read_game_trace':
      return 'Play-testing your game';
    case 'read_console':
      return 'Checking for problems';
    case 'prism_sound_render':
    case 'prism_sound_compare':
      return 'Listening to the sound';
    case 'prism_model_edit':
      return 'Sculpting the model';
    case 'prism_model_rig':
      return 'Rigging the character';
    case 'prism_model_template':
      return 'Starting a new model';
    case 'create_task':
    case 'assign_task':
    case 'update_task':
    case 'read_board':
      return 'Planning the next steps';
    case 'memory_write':
    case 'memory_read':
    case 'memory_forget':
    case 'read_working_memory':
    case 'save_checkpoint':
      return 'Making notes';
    case 'spawn_agents':
      return 'Getting the team on it';
    case 'finish':
      return 'Wrapping up';
    default:
      if (tool.includes('video')) return 'Making a video';
      if (tool.includes('pixel')) return 'Drawing pixel art';
      if (tool.includes('model')) return 'Creating a 3D model';
      if (tool.includes('sound') || tool.includes('audio')) return 'Making sounds';
      if (tool.includes('generat')) return 'Creating art';
      return 'Working on it';
  }
}

/** Bookkeeping that means nothing to the person watching. */
const QUIET_TOOLS = new Set(['update_working_memory', 'read_working_memory', 'save_checkpoint', 'compact_context', 'memory_read', 'memory_write', 'memory_forget']);

/** One line of Prisma's streamlined chat: a message, or a run of her steps between them. */
export type PrismaFeedItem =
  | { readonly kind: 'entry'; readonly entry: ActivityEntry }
  | { readonly kind: 'steps'; readonly id: number; readonly steps: readonly string[] };

/**
 * What a person sees of Prisma's chat: their messages, her replies and the
 * pictures her tools made, with the work in between folded into runs of
 * steps in her words ("Reading player", "Play-testing your game"). Thinking,
 * notes and errors stay out; repeats of the same step show once.
 */
export function prismaFeed(entries: readonly ActivityEntry[], memberId: number, hasPictures: (text: string) => boolean): PrismaFeedItem[] {
  const items: PrismaFeedItem[] = [];
  for (const entry of entries) {
    if (entry.memberId !== 0 && entry.kind === 'tool' && !hasPictures(entry.text)) {
      if (entry.memberId !== memberId || !entry.tool || QUIET_TOOLS.has(entry.tool)) continue;
      const step = describeTool(entry.tool, entry.arguments);
      const last = items[items.length - 1];
      if (last?.kind === 'steps') {
        if (last.steps[last.steps.length - 1] !== step) items[items.length - 1] = { ...last, steps: [...last.steps, step] };
      } else items.push({ kind: 'steps', id: entry.id, steps: [step] });
      continue;
    }
    const shown =
      entry.memberId === 0 ||
      entry.kind === 'tool' ||
      ((entry.kind === 'message' || entry.kind === 'output') && (entry.stream ?? 'assistant') === 'assistant');
    if (shown) items.push({ kind: 'entry', entry });
  }
  return items;
}

export type PrismaMood = 'idle' | 'thinking' | 'working' | 'writing';

/**
 * Prisma's mood and status line for her current turn: the newest entry of her
 * chat after the latest message from the user.
 */
export function prismaStatus(entries: readonly ActivityEntry[], memberId: number, running: boolean): { mood: PrismaMood; line: string } {
  if (!running) return { mood: 'idle', line: 'Ready when you are' };
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i]!;
    if (e.memberId === 0) break;
    if (e.memberId !== memberId) continue;
    if (e.kind === 'tool' && e.tool) return { mood: 'working', line: describeTool(e.tool, e.arguments) };
    if (e.kind === 'output' && e.stream === 'thinking') return { mood: 'thinking', line: 'Thinking it through' };
    if (e.kind === 'output' && e.stream === 'assistant') return { mood: 'writing', line: 'Writing back' };
  }
  return { mood: 'thinking', line: 'Thinking it through' };
}
